import { useEffect, useRef } from 'react';
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
  ApiResponse,
  MarkAllReadInput,
  Notification,
  NotificationGroupItems,
  NotificationGroupPage,
  NotificationPreference,
  NotificationUserSettings,
} from '@task-tracker/shared';
import {
  notificationGroupKey,
  notificationKeys,
  shouldSendPush,
  DEFAULT_NOTIFICATION_USER_SETTINGS,
  NOTIFICATION_GROUP_PAGE_SIZE,
  NOTIFICATION_GROUP_PREVIEW_COUNT,
  NOTIFICATION_REFETCH_MS,
} from '@task-tracker/shared';
import {
  getDesktopNotificationPermission,
  isDesktopNotificationEnabled,
  showDesktopNotification,
} from '../lib/desktop-notifications';
import { apiClient } from '../lib/api-client';
import { supabase } from '../lib/supabase';
import { CACHE_POLICY } from '../lib/cache-policy';
import { useNotificationSettings, notificationSettingsKey } from './use-notification-settings';
import { useAuthStore } from '../stores/auth.store';
import { myTasksKeys } from './use-my-tasks';
import { invalidateProjectRecentUpdates } from './use-project-recent-updates';
import { getNotificationRoute } from '../components/notifications/notification-display';
import {
  useTaskHierarchyNavigation,
  type TaskHierarchyTarget,
} from './use-task-hierarchy-navigation';

export { notificationKeys };
// Settings live in their own module — one owner for the key, see the note
// there. Re-exported so callers already importing from here keep working.
export { useNotificationSettings, notificationSettingsKey };

const TASK_CHAIN_TYPES = new Set([
  'task_assigned',
  'task_created',
  'task_updated',
  'task_status_changed',
  'task_ready_to_start',
  'task_completed',
  'task_overdue',
  'task_at_risk',
  'task_reassigned',
  'task_reopened',
  'deadline_reminder',
  'escalation_alert',
  'note_created',
  'comment_reply',
  'mention',
]);

// ── Notification list ────────────────────────────────────────────────────────

export function useNotificationList(filters?: {
  unread_only?: boolean;
  type?: string | readonly string[];
  page?: number;
  pageSize?: number;
}) {
  // Unread-only is forced here rather than at each call site: every
  // notification surface is an inbox now — reading an item removes it. The
  // read item is not lost, it lives on in the project's Notifications tab.
  // A caller can still opt OUT by passing `unread_only: false` explicitly —
  // the home Mentions panel does, since a mention stays true after it's read.
  const effectiveFilters = { ...filters, unread_only: filters?.unread_only ?? true };
  const queryString = new URLSearchParams(
    Object.entries(effectiveFilters)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, String(v)]),
  ).toString();

  return useQuery({
    queryKey: notificationKeys.list(effectiveFilters),
    queryFn: () =>
      apiClient<{
        notifications: Notification[];
        pagination: { page: number; pageSize: number; total: number };
      }>(`/api/v1/notifications${queryString ? `?${queryString}` : ''}`),
    ...CACHE_POLICY.live,
  });
}

// ── Grouped notification list ────────────────────────────────────────────────

export interface NotificationGroupFilters {
  unread_only?: boolean;
  type?: string | readonly string[];
  search?: string;
}

/** Every grouped surface is unread-only by default — see useNotificationList.
 *  A caller can opt out by passing `unread_only: false` explicitly (the
 *  Mentions tab does). Applied to the query key too, so the cache never holds
 *  a mixed read/unread page under one key. */
function withUnreadOnly(filters: NotificationGroupFilters): NotificationGroupFilters {
  return { ...filters, unread_only: filters.unread_only ?? true };
}

function groupQueryString(filters: NotificationGroupFilters, extra: Record<string, string> = {}) {
  const params = new URLSearchParams(extra);
  if (filters.unread_only) params.set('unread_only', 'true');
  if (filters.type?.length) {
    params.set('type', typeof filters.type === 'string' ? filters.type : filters.type.join(','));
  }
  // Both hooks go through here, so search reaches the expander too — otherwise
  // a group's "Show N more" count would be search-scoped while expanding it
  // returned everything.
  if (filters.search) params.set('search', filters.search);
  return params.toString();
}

/**
 * The project-clustered list. Pages by whole groups rather than rows, so a
 * project's notifications never straddle two fetches, and a new notification
 * lifts its entire group to the top.
 */
export function useNotificationGroups(rawFilters: NotificationGroupFilters = {}) {
  const filters = withUnreadOnly(rawFilters);
  return useInfiniteQuery({
    queryKey: notificationKeys.groups(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const query = groupQueryString(filters, {
        limit: String(NOTIFICATION_GROUP_PAGE_SIZE),
        preview: String(NOTIFICATION_GROUP_PREVIEW_COUNT),
        ...(pageParam ? { cursor: pageParam } : {}),
      });
      return apiClient<NotificationGroupPage>(`/api/v1/notifications/groups?${query}`);
    },
    getNextPageParam: (lastPage) => lastPage.data?.nextCursor ?? undefined,
    // Typing changes the query key, which would otherwise drop the whole list
    // back to a pending state on every keystroke — blanking the results (and,
    // on mobile, unmounting the search box itself) between terms.
    placeholderData: (previous) => previous,
    staleTime: CACHE_POLICY.live.staleTime,
    gcTime: CACHE_POLICY.live.gcTime,
  });
}

/** One expanded group's full notification list, behind "Show N more". */
export function useNotificationGroupItems(
  projectId: string | null,
  rawFilters: NotificationGroupFilters = {},
  options: { enabled?: boolean } = {},
) {
  const filters = withUnreadOnly(rawFilters);
  return useQuery({
    queryKey: notificationKeys.groupItems(projectId, filters),
    enabled: options.enabled ?? true,
    queryFn: () => {
      const query = groupQueryString(filters, projectId ? { project_id: projectId } : {});
      return apiClient<NotificationGroupItems>(
        `/api/v1/notifications/groups/items${query ? `?${query}` : ''}`,
      );
    },
    ...CACHE_POLICY.live,
  });
}

// ── Unread count ─────────────────────────────────────────────────────────────

export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () =>
      apiClient<{ unreadCount: number; mentionCount: number }>(
        '/api/v1/notifications/unread-count',
      ),
    refetchInterval: NOTIFICATION_REFETCH_MS,
  });
}

// ── Mark as read ─────────────────────────────────────────────────────────────

export function useMarkAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: (_result, notificationId) => {
      markNotificationReadInGroupCache(queryClient, notificationId);
      void queryClient.invalidateQueries({ queryKey: notificationKeys.listRoot() });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      toast.success('Notification marked as read');
    },
  });
}

async function markNotificationRead(notificationId: string) {
  return apiClient(`/api/v1/notifications/${notificationId}/read`, { method: 'PATCH' });
}

/**
 * Patches the grouped cache in place instead of invalidating it.
 *
 * Invalidating an infinite query refetches every loaded page — sequentially,
 * at ~258ms a hop — on the most common action on this screen. Worse, each page
 * refetches with the cursor it was originally fetched with: if page 1 shrank
 * (guaranteed under unread_only, where marking read removes the row), page 2's
 * stored boundary now skips whole groups. The de-dupe in
 * flattenNotificationGroupPages can absorb a duplicate; it cannot recover a gap.
 */
function markNotificationReadInGroupCache(
  queryClient: ReturnType<typeof useQueryClient>,
  notificationId: string,
) {
  const owningGroupKey = findGroupKeyForNotification(queryClient, notificationId);

  queryClient.setQueriesData<InfiniteData<ApiResponse<NotificationGroupPage>>>(
    { queryKey: notificationKeys.groupsRoot() },
    (cached) => {
      if (!cached) return cached;

      // The row is REMOVED, not dimmed: these lists are unread-only now, so a
      // read notification does not belong here at all. Disappearing is the
      // requested behavior, and the item remains in the project's
      // Notifications tab. A group whose last unread row goes drops out too,
      // otherwise an empty card lingers.
      return {
        ...cached,
        pages: cached.pages.map((page) => ({
          ...page,
          data: {
            ...page.data,
            groups: page.data.groups.flatMap((group) => {
              const inPreview = group.notifications.some((n) => n.id === notificationId);
              // An expanded group renders its own fetched list in place of the
              // preview, so a row marked read from there is not in
              // `notifications` at all — without the owning-key fallback the
              // header badge would silently stop tracking.
              if (!inPreview && notificationGroupKey(group) !== owningGroupKey) return [group];

              const unreadCount = Math.max(0, group.unreadCount - 1);
              if (unreadCount === 0) return [];

              return [
                {
                  ...group,
                  unreadCount,
                  total: Math.max(0, group.total - 1),
                  notifications: group.notifications.filter((n) => n.id !== notificationId),
                },
              ];
            }),
          },
        })),
      };
    },
  );

  // An expanded group holds its own copy of the row.
  queryClient.setQueriesData<ApiResponse<NotificationGroupItems>>(
    { queryKey: notificationKeys.groupItemsRoot() },
    (cached) =>
      cached
        ? {
            ...cached,
            data: {
              ...cached.data,
              items: cached.data.items.filter((n) => n.id !== notificationId),
              total: Math.max(0, cached.data.total - 1),
            },
          }
        : cached,
  );
}

/** Which group's expanded list currently holds this notification, if any. The
 *  group key is the third element of a `groupItems` query key. */
function findGroupKeyForNotification(
  queryClient: ReturnType<typeof useQueryClient>,
  notificationId: string,
): string | undefined {
  const matches = queryClient.getQueriesData<ApiResponse<NotificationGroupItems>>({
    queryKey: notificationKeys.groupItemsRoot(),
  });

  for (const [key, cached] of matches) {
    const stillUnread = cached?.data?.items?.some((n) => n.id === notificationId && !n.is_read);
    if (stillUnread) return key[2] as string;
  }
  return undefined;
}

function invalidateNotificationQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  notificationId?: string,
) {
  // Same reasoning as markNotificationReadInGroupCache: patch the grouped
  // cache, don't invalidate it.
  if (notificationId) markNotificationReadInGroupCache(queryClient, notificationId);
  void queryClient.invalidateQueries({ queryKey: notificationKeys.listRoot() });
  void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
}

export function useOpenNotification() {
  const navigate = useNavigate();
  const location = useLocation();
  const openTaskHierarchy = useTaskHierarchyNavigation();
  const queryClient = useQueryClient();

  return async (notification: Notification): Promise<boolean> => {
    const taskTarget = resolveNotificationTaskTarget(notification);
    const opened = taskTarget
      ? await openTaskHierarchy(taskTarget, { mode: 'shortcut' })
      : openFallbackNotificationRoute(
          navigate,
          notification,
          location.pathname === '/notifications',
        );

    if (!opened) return false;

    if (notification.id) {
      try {
        await markNotificationRead(notification.id);
        invalidateNotificationQueries(queryClient, notification.id);
      } catch {
        // Opening the destination is more important than blocking on read-state sync.
      }
    }

    return true;
  };
}

function resolveNotificationTaskTarget(notification: Notification): TaskHierarchyTarget | null {
  if (!TASK_CHAIN_TYPES.has(notification.type)) return null;

  const data = notification.data ?? {};
  const taskId = readString(data, ['taskId', 'task_id']);
  const payloadProjectId = readString(data, ['projectId', 'project_id']);
  const payloadParentTaskId = readOptionalString(data, ['parentTaskId', 'parent_task_id']);

  if (!taskId) return null;

  const target: TaskHierarchyTarget = { taskId };
  if (payloadProjectId) target.projectId = payloadProjectId;
  if (payloadParentTaskId !== undefined) target.parentTaskId = payloadParentTaskId;
  return target;
}

function openFallbackNotificationRoute(
  navigate: ReturnType<typeof useNavigate>,
  notification: Notification,
  replaceProjectsRoot: boolean,
): boolean {
  const fallbackRoute = getNotificationRoute(notification.type, notification.data);
  if (!fallbackRoute) return false;

  if (fallbackRoute.startsWith('/projects/')) {
    openRouteChain(navigate, ['/projects', fallbackRoute], replaceProjectsRoot);
    return true;
  }

  navigate(fallbackRoute);
  return true;
}

function openRouteChain(
  navigate: ReturnType<typeof useNavigate>,
  routeChain: string[],
  replaceProjectsRoot: boolean,
) {
  const [firstRoute, ...nextRoutes] = routeChain;
  if (!firstRoute) return;

  navigate(firstRoute, { replace: replaceProjectsRoot && firstRoute === '/projects' });

  for (const route of nextRoutes) {
    navigate(route);
  }
}

function readString(data: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return undefined;
}

function readOptionalString(
  data: Record<string, unknown>,
  keys: string[],
): string | null | undefined {
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
    const value = data[key];
    if (value === null) return null;
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return undefined;
}

// ── Mark all as read ─────────────────────────────────────────────────────────

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markAllRead({ scope: 'all' }),
    onSuccess: () => {
      // A bulk flip touches rows well outside the loaded pages, so here a full
      // reset is the honest move — and resetQueries collapses to page 1 rather
      // than refetching every loaded page against stale cursors.
      void queryClient.resetQueries({ queryKey: notificationKeys.groupsRoot() });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.listRoot() });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      toast.success('All notifications marked as read');
    },
  });
}

/** Mark one project's cluster read from its group header. */
export function useMarkGroupAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string | null) =>
      markAllRead(projectId ? { scope: 'project', project_id: projectId } : { scope: 'general' }),
    onSuccess: () => {
      void queryClient.resetQueries({ queryKey: notificationKeys.groupsRoot() });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.listRoot() });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      toast.success('Marked as read');
    },
  });
}

function markAllRead(body: MarkAllReadInput) {
  return apiClient<{ markedAsReadCount: number }>('/api/v1/notifications/read-all', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// ── Supabase Realtime subscription ──────────────────────────────────────────

/**
 * Subscribes to real-time INSERT events on the notifications table for the
 * current user. When a new notification arrives, TanStack Query caches are
 * invalidated so the bell badge and list refresh instantly.
 *
 * Mount this hook once in a top-level component (e.g. NotificationBell).
 *
 * Delivery splits on whether the user is actually looking at the tab: focused
 * gets the in-app sonner toast, unfocused gets a native OS notification (so a
 * backgrounded tab or a minimised Chrome still reaches them).
 */
export function useRealtimeNotifications() {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const { data: settingsData } = useNotificationSettings();
  const openNotification = useOpenNotification();

  // Held in refs so the Realtime effect keeps its [userId, queryClient] deps:
  // adding these would tear down and resubscribe the channel on every render.
  const settingsRef = useRef<NotificationUserSettings>(DEFAULT_NOTIFICATION_USER_SETTINGS);
  const openNotificationRef = useRef(openNotification);
  useEffect(() => {
    settingsRef.current = settingsData?.settings ?? DEFAULT_NOTIFICATION_USER_SETTINGS;
    openNotificationRef.current = openNotification;
  });

  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`notifications-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          // Collapse the grouped list back to page 1 and refetch just that
          // page. Invalidating instead would refetch every loaded page
          // sequentially against cursors the new row has already invalidated —
          // and "the group jumps to the top" IS a reset to page 1, so this is
          // also the semantically correct move. It costs the reader their
          // loaded pages, which at this notification volume is rare.
          void queryClient.resetQueries({ queryKey: notificationKeys.groupsRoot() });
          void queryClient.invalidateQueries({ queryKey: notificationKeys.listRoot() });
          void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });

          // Show a toast for the new notification
          const newNotif = payload.new as Notification;
          // The project's Notifications tab renders this row too, and a user
          // looking at it while a notification lands should see it appear.
          invalidateProjectRecentUpdates(queryClient, newNotif?.project_id);
          if (newNotif?.type === 'task_assigned') {
            void queryClient.invalidateQueries({ queryKey: myTasksKeys.list() });
          }
          // `visibilityState === 'visible'` alone is true whenever the tab is
          // frontmost in a non-minimised window — including when Chrome sits
          // behind another app entirely. hasFocus() is what "the user is
          // actually looking at it" means.
          const tabFocused = document.visibilityState === 'visible' && document.hasFocus();

          // The in-app row is inserted before the API applies category mutes
          // and quiet hours (those gate push/email only), so Realtime fires
          // regardless. An OS notification is push-grade, so it has to be
          // gated by the same shared rule the API uses for FCM.
          // Read straight from localStorage at delivery time rather than
          // mirroring it through state: the Settings toggle renders its own
          // instance of useDesktopNotifications, so a mirrored copy here would
          // stay false until the next reload.
          const delivered =
            !tabFocused &&
            isDesktopNotificationEnabled(userId) &&
            getDesktopNotificationPermission() === 'granted' &&
            shouldSendPush(settingsRef.current, newNotif.type, new Date()) &&
            showDesktopNotification(newNotif, () => {
              void openNotificationRef.current(newNotif);
            });

          // Falls back to the toast whenever the native path declined — a
          // blocked, titleless or throwing notification must not drop the alert.
          if (!delivered && newNotif?.title) {
            toast(newNotif.title, {
              description: newNotif.body ?? undefined,
            });
          }
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);
}

// ── Preferences ──────────────────────────────────────────────────────────────

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: () =>
      apiClient<{ preferences: NotificationPreference[] }>('/api/v1/notifications/preferences'),
  });
}

export function useUpdatePreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { channel: string; enabled: boolean }) =>
      apiClient('/api/v1/notifications/preferences', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
      toast.success('Preference updated');
    },
  });
}
